import {
  Activity,
  BarChart3,
  BookText,
  Bus,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  FileClock,
  FileStack,
  FileText,
  Gift,
  Handshake,
  Home,
  IdCard,
  Inbox,
  LayoutDashboard,
  Library,
  LifeBuoy,
  ListChecks,
  MapPin,
  Newspaper,
  Sparkles,
  SlidersHorizontal,
  TrendingUp,
  Users,
  UserRoundCheck,
  UserRoundPlus,
  Video,
  type LucideIcon,
} from "lucide-react"

// The admin nav, as data. Every href comes from `usePage().props.routes`, which
// the server nils out for anything the current user can't reach — so a section
// with no reachable items disappears on its own. That's how the Content section
// stays hidden until the Insights admin moves off its dev-only routes, and how
// Platform stays super-admin-only, without any role checks out here.
export type NavRouteKey =
  | "driver_home"
  | "packet"
  | "profile"
  | "forms"
  | "calendar"
  | "refer"
  | "referrals"
  | "help_requests"
  | "reports"
  | "seo"
  | "school_year"
  | "districts"
  | "vehicles"
  | "dashboard"
  | "applications"
  | "drivers"
  | "driver_statuses"
  | "team"
  | "workflows"
  | "form_submissions"
  | "documents"
  | "credentials"
  | "driver_resources"
  | "videos"
  | "insights_dashboard"
  | "insights_review"
  | "insights_strategy"
  | "insights_facts"
  | "platform_overview"
  | "organizations"
  | "document_templates"
  | "flightdeck"

export type NavCountKey = "applications" | "submissions" | "outstanding" | "alerts" | "help_requests"

export type NavItemSpec = {
  route: NavRouteKey
  label: string
  icon: LucideIcon
  /** Which `nav_counts` value this item shows. Absent = never carries a count. */
  countKey?: NavCountKey
  /**
   * Leaves the SPA. For a route that isn't an Inertia page — a mounted engine —
   * an Inertia `<Link>` would XHR for a JSON page object, get HTML, and fail.
   * These rows render a plain anchor and do a real navigation.
   */
  external?: boolean
}

export type NavSectionSpec = {
  /** Stable id — persists this section's collapsed state. */
  id: string
  label: string | null
  items: NavItemSpec[]
}

export const NAV: NavSectionSpec[] = [
  {
    id: "overview",
    label: null, // Dashboard stands alone: it's the whole answer, not a category.
    // Integrity alerts have no page of their own — the Dashboard is where they're
    // triaged (BUSINESS_RULES §5.2), so it carries the count. Rare and urgent:
    // a tampered or unverifiable signed PDF is a security matter, not paperwork.
    items: [{ route: "dashboard", label: "Dashboard", icon: LayoutDashboard, countKey: "alerts" }],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      { route: "school_year", label: "School Year", icon: CalendarDays },
      { route: "districts", label: "Districts", icon: MapPin },
      { route: "reports", label: "Reports", icon: BarChart3 },
      { route: "seo", label: "Site Performance", icon: TrendingUp },
      { route: "vehicles", label: "Vehicles", icon: Bus },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      { route: "applications", label: "Applications", icon: UserRoundPlus, countKey: "applications" },
      { route: "drivers", label: "Drivers", icon: Users, countKey: "outstanding" },
      // Its own permission, so it's its own item — a carrier admin who works the
      // roster daily may well not be the person who decides what the statuses
      // mean. The Drivers page keeps its "Statuses" button too; that's the
      // in-context route, this is the one you can find without knowing to look.
      { route: "driver_statuses", label: "Driver Statuses", icon: SlidersHorizontal },
      { route: "team", label: "Team", icon: UserRoundCheck },
      { route: "referrals", label: "Referrals", icon: Handshake },
      { route: "help_requests", label: "Help Requests", icon: LifeBuoy, countKey: "help_requests" },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    items: [
      { route: "workflows", label: "Workflows", icon: ListChecks },
      { route: "documents", label: "Documents", icon: FileClock },
      { route: "credentials", label: "Credentials", icon: CalendarClock },
      { route: "form_submissions", label: "Submissions", icon: Inbox, countKey: "submissions" },
    ],
  },
  {
    id: "library",
    label: "Library",
    items: [
      { route: "driver_resources", label: "Resources", icon: Library },
      { route: "videos", label: "Videos", icon: Video },
    ],
  },
  {
    id: "content",
    label: "Content",
    items: [
      { route: "insights_dashboard", label: "Overview", icon: Sparkles },
      { route: "insights_strategy", label: "Strategy", icon: SlidersHorizontal },
      { route: "insights_facts", label: "Facts", icon: BookText },
      { route: "insights_review", label: "Review", icon: Newspaper },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    items: [
      { route: "platform_overview", label: "Overview", icon: LayoutDashboard },
      { route: "organizations", label: "Organizations", icon: Building2 },
      { route: "document_templates", label: "Documents", icon: FileStack },
      // Solid Queue's dashboard — a mounted engine, so it leaves the SPA.
      { route: "flightdeck", label: "Job Queue", icon: Activity, external: true },
    ],
  },
]

// The nav for org members without admin powers — drivers and staff. Same
// route-gating as NAV: `packet` is nil for staff (they have no packet), so they
// see only Forms & Resources, while a driver sees both. Keeps the sidebar from
// standing empty for the people who live in the packet and the filing desk.
export const MEMBER_NAV: NavSectionSpec[] = [
  {
    id: "member",
    label: null,
    items: [
      { route: "driver_home", label: "Home", icon: Home },
      { route: "packet", label: "My Documents", icon: FileText },
      { route: "profile", label: "My Profile", icon: IdCard },
      { route: "forms", label: "Forms & Resources", icon: ClipboardList },
      { route: "calendar", label: "Calendar", icon: CalendarDays },
      { route: "refer", label: "Refer a Friend", icon: Gift },
    ],
  },
]
